import React from 'react';
import { Filter, Plane, Sunrise, Sun, Sunset, Moon } from 'lucide-react';

const RoundTripFilterSidebar = ({
  selectedStops,
  setSelectedStops,
  selectedAirlines,
  setSelectedAirlines,
  selectedOnwardDepartureTimes,
  setSelectedOnwardDepartureTimes,
  selectedOnwardArrivalTimes,
  setSelectedOnwardArrivalTimes,
  selectedReturnDepartureTimes,
  setSelectedReturnDepartureTimes,
  selectedReturnArrivalTimes,
  setSelectedReturnArrivalTimes,
  priceRange,
  setPriceRange,
  stopCounts,
  uniqueAirlines,
  airlineLogos = {}
}) => {
  const toggleStop = (stop) => {
    if (selectedStops.includes(stop)) {
      setSelectedStops(selectedStops.filter(s => s !== stop));
    } else {
      setSelectedStops([...selectedStops, stop]);
    }
  };

  const toggleAirline = (code) => {
    if (selectedAirlines.includes(code)) {
      setSelectedAirlines(selectedAirlines.filter(a => a !== code));
    } else {
      setSelectedAirlines([...selectedAirlines, code]);
    }
  };

  const toggleOnwardDepartureTime = (timeId) => {
    if (selectedOnwardDepartureTimes?.includes(timeId)) {
      setSelectedOnwardDepartureTimes(selectedOnwardDepartureTimes.filter(t => t !== timeId));
    } else {
      setSelectedOnwardDepartureTimes([...(selectedOnwardDepartureTimes || []), timeId]);
    }
  };

  const toggleOnwardArrivalTime = (timeId) => {
    if (selectedOnwardArrivalTimes?.includes(timeId)) {
      setSelectedOnwardArrivalTimes(selectedOnwardArrivalTimes.filter(t => t !== timeId));
    } else {
      setSelectedOnwardArrivalTimes([...(selectedOnwardArrivalTimes || []), timeId]);
    }
  };

  const toggleReturnDepartureTime = (timeId) => {
    if (selectedReturnDepartureTimes?.includes(timeId)) {
      setSelectedReturnDepartureTimes(selectedReturnDepartureTimes.filter(t => t !== timeId));
    } else {
      setSelectedReturnDepartureTimes([...(selectedReturnDepartureTimes || []), timeId]);
    }
  };

  const toggleReturnArrivalTime = (timeId) => {
    if (selectedReturnArrivalTimes?.includes(timeId)) {
      setSelectedReturnArrivalTimes(selectedReturnArrivalTimes.filter(t => t !== timeId));
    } else {
      setSelectedReturnArrivalTimes([...(selectedReturnArrivalTimes || []), timeId]);
    }
  };

  const timeSlots = [
    { id: 'earlyMorning', label: 'Early Morning', time: '00:00 - 06:00', icon: Moon },
    { id: 'morning', label: 'Morning', time: '06:00 - 12:00', icon: Sunrise },
    { id: 'afternoon', label: 'Afternoon', time: '12:00 - 18:00', icon: Sun },
    { id: 'evening', label: 'Evening', time: '18:00 - 24:00', icon: Sunset }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-5 sticky top-5">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-[#1B375B] flex items-center gap-2">
          <Filter className="w-5 h-5 text-[#659A9B]" />
          Filters
        </h2>
        {(selectedStops.length > 0 || selectedAirlines.length > 0 || selectedOnwardDepartureTimes?.length > 0 || selectedOnwardArrivalTimes?.length > 0 || selectedReturnDepartureTimes?.length > 0 || selectedReturnArrivalTimes?.length > 0) && (
          <button
            onClick={() => {
              setSelectedStops([]);
              setSelectedAirlines([]);
              setSelectedOnwardDepartureTimes?.([]);
              setSelectedOnwardArrivalTimes?.([]);
              setSelectedReturnDepartureTimes?.([]);
              setSelectedReturnArrivalTimes?.([]);
              setPriceRange([5000, 70000]);
            }}
            className="text-[10px] text-[#659A9B] font-bold uppercase tracking-wider hover:text-[#1B375B] transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Stops */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Stops</p>
          <div className="space-y-1.5">
            {[
              { id: 0, label: 'Direct', count: stopCounts?.nonStop || 0 },
              { id: 1, label: '1 Stop', count: stopCounts?.oneStop || 0 },
              { id: 2, label: '2+ Stops', count: stopCounts?.twoPlus || 0 }
            ].map((stop) => (
              <button
                key={stop.id}
                onClick={() => toggleStop(stop.id)}
                className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl text-left transition-all ${
                  selectedStops.includes(stop.id) ? 'bg-[#659A9B]/10 ring-1 ring-[#659A9B]/30' : 'hover:bg-[#E7EEEE]/60'
                }`}
              >
                <span className={`text-xs ${selectedStops.includes(stop.id) ? 'font-bold text-[#1B375B]' : 'font-semibold text-gray-600'}`}>
                  {stop.label}
                </span>
                <span className="text-[10px] text-gray-400 font-medium">({stop.count})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="h-[1px] bg-gray-100" />

        {/* Onward Departure Time */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Onward Departure</p>
          <div className="grid grid-cols-2 gap-2">
            {timeSlots.map((slot) => {
              const isSelected = selectedOnwardDepartureTimes?.includes(slot.id);
              const IconComponent = slot.icon;
              return (
                <button
                  key={slot.id}
                  onClick={() => toggleOnwardDepartureTime(slot.id)}
                  className={`p-2.5 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-[#659A9B]/10 ring-2 ring-[#659A9B]/40'
                      : 'bg-[#E7EEEE]/60 hover:bg-[#E7EEEE]'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <IconComponent className={`w-3.5 h-3.5 ${isSelected ? 'text-[#659A9B]' : 'text-gray-400'}`} />
                    <span className={`text-xs font-semibold ${isSelected ? 'text-[#1B375B]' : 'text-gray-600'}`}>
                      {slot.label}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400">{slot.time}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-[1px] bg-gray-100" />

        {/* Onward Arrival Time */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Onward Arrival</p>
          <div className="grid grid-cols-2 gap-2">
            {timeSlots.map((slot) => {
              const isSelected = selectedOnwardArrivalTimes?.includes(slot.id);
              const IconComponent = slot.icon;
              return (
                <button
                  key={slot.id}
                  onClick={() => toggleOnwardArrivalTime(slot.id)}
                  className={`p-2.5 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-[#659A9B]/10 ring-2 ring-[#659A9B]/40'
                      : 'bg-[#E7EEEE]/60 hover:bg-[#E7EEEE]'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <IconComponent className={`w-3.5 h-3.5 ${isSelected ? 'text-[#659A9B]' : 'text-gray-400'}`} />
                    <span className={`text-xs font-semibold ${isSelected ? 'text-[#1B375B]' : 'text-gray-600'}`}>
                      {slot.label}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400">{slot.time}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-[1px] bg-gray-100" />

        {/* Return Departure Time */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Return Departure</p>
          <div className="grid grid-cols-2 gap-2">
            {timeSlots.map((slot) => {
              const isSelected = selectedReturnDepartureTimes?.includes(slot.id);
              const IconComponent = slot.icon;
              return (
                <button
                  key={slot.id}
                  onClick={() => toggleReturnDepartureTime(slot.id)}
                  className={`p-2.5 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-[#659A9B]/10 ring-2 ring-[#659A9B]/40'
                      : 'bg-[#E7EEEE]/60 hover:bg-[#E7EEEE]'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <IconComponent className={`w-3.5 h-3.5 ${isSelected ? 'text-[#659A9B]' : 'text-gray-400'}`} />
                    <span className={`text-xs font-semibold ${isSelected ? 'text-[#1B375B]' : 'text-gray-600'}`}>
                      {slot.label}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400">{slot.time}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-[1px] bg-gray-100" />

        {/* Return Arrival Time */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Return Arrival</p>
          <div className="grid grid-cols-2 gap-2">
            {timeSlots.map((slot) => {
              const isSelected = selectedReturnArrivalTimes?.includes(slot.id);
              const IconComponent = slot.icon;
              return (
                <button
                  key={slot.id}
                  onClick={() => toggleReturnArrivalTime(slot.id)}
                  className={`p-2.5 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-[#659A9B]/10 ring-2 ring-[#659A9B]/40'
                      : 'bg-[#E7EEEE]/60 hover:bg-[#E7EEEE]'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <IconComponent className={`w-3.5 h-3.5 ${isSelected ? 'text-[#659A9B]' : 'text-gray-400'}`} />
                    <span className={`text-xs font-semibold ${isSelected ? 'text-[#1B375B]' : 'text-gray-600'}`}>
                      {slot.label}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400">{slot.time}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-[1px] bg-gray-100" />

        {/* Airlines */}
        <div>
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-2.5">Airlines</p>
          <div className="space-y-1">
            {uniqueAirlines?.map((airline) => {
              const isSelected = selectedAirlines.includes(airline.code);
              return (
                <button
                  key={airline.code}
                  onClick={() => toggleAirline(airline.code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all ${
                    isSelected ? 'bg-[#659A9B]/10 ring-1 ring-[#659A9B]/30' : 'hover:bg-[#E7EEEE]/60'
                  }`}
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    {airlineLogos[airline.code]?.square ? (
                      <img src={airlineLogos[airline.code].square} alt={airline.code} className="w-full h-full object-contain" />
                    ) : (
                      <Plane className="w-3.5 h-3.5 text-[#659A9B]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs block ${isSelected ? 'font-bold text-[#1B375B]' : 'font-semibold text-gray-600'}`}>
                      {airline.name}
                    </span>
                    <span className="text-[10px] text-gray-400">{airline.count} flights</span>
                  </div>
                  {isSelected && (
                    <div className="w-4 h-4 rounded-full bg-[#659A9B] flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoundTripFilterSidebar;
